import React, {createRef} from 'react'
import ReactDOM from 'react-dom'
import PopoverContainer from "./popover-container"
import {popoverArrow_align, popoverArrow_keepAttachClassesWhenPinned, popoverArrow_overlapsTarget} from "./popover-arrow-align"
import VScroll from "../scrolling/custom-v-scroll/v-scroll"
import {BUS_KEYBOARD_ESCAPE} from "../../gd/keyboard-shortcuts/keyboard-shortcuts";
import PopoverPosition from "./popover-position";
import {PopoverHeightRestriction} from "./@const";


type Props = {

    position                    : PopoverPosition,

    className?                  : string,
    style?                      : any
    children?                   : any

    rootSelector?               : string,

    popoverClass?               : string,
    popoverWith?                : number,      // if set will add width as a style

    popoverContentStyle?        : any,


    targetClass?                : string,  // class of element we want attach to


    onKeyDown?                  : callback,
    onMouseEnter?               : callback,
    onMouseLeave?               : callback,
    onClick?                    : callback,
    onClickOutside?             : callback,
    outsideClickIgnoreClass?    : string,
    clickOutsideEventTypes?     : any,
    clickOutsideDelay?          : number,
    onPopoverHide?              : callback,

    getTargetFunc?              : callback,
    onContainerReference?       : any,

    enableArrows?               : boolean,  // show arrows

    arrowsVersion?              : 1 | 2,
    arrowTarget?                : string // point on the anchor the arrow tip aims at: "<top|middle|bottom> <left|center|right>", default "middle center"
    arrowPointOffset?           : string // "dy dx" in px — shifts the computed arrow tip point, e.g. "4 0"
    arrowEdgeInset?             : number // master-style arrow: px in from the popover's attached edge, pinned to the anchor (overrides arrowTarget)
    fitTargetWidth?             : boolean,
    heightRestriction?          : PopoverHeightRestriction,     // max height of popover
    constraints?                : any[]




    autoVisibleTarget?          : boolean  // not in use I think safe to remove

}

// const debug = "Popover"
const debug = false

export default class Popover extends React.Component<Props> {

    public displayName      : string
    public _popoverElement  : any
    public _tether          : any

    private popoverArrowRef : any = createRef()

    static defaultProps: Partial<Props> = {
        rootSelector: "body",

        // targetClass: "pop-target",

        constraints: [
            {
                to          : "window",
                attachment  : "together",
                pin         : true
            },
        ],

        enableArrows: true,
        arrowsVersion: 2,
        fitTargetWidth: false,
        outsideClickIgnoreClass: 'ignore-react-onclickoutside', // 'popover-container', //'ignore-react-onclickoutside',
        clickOutsideEventTypes: [/*'click', 'mouseup',*/ 'mousedown', 'touchstart'],
        clickOutsideDelay: 200,
        heightRestriction: PopoverHeightRestriction.NONE
    };

    constructor(props:Props) {
        super(props);

        this.displayName = "Popover";

        const popover = document.createElement( "span" );
        popover.className = Classes(props.popoverClass, props.position.attachmentType, "popover", {
            ['with-arrows arrows-version-{0}'.f(props.arrowsVersion)]: props.enableArrows,
        });


        if (props.popoverWith) {
            popover.style.width = props.popoverWith + 'px';
        }

        popover.onclick = (e)=>{if (props.onClick) props.onClick(e)};
        popover.onmouseenter = (e)=>{
            if (props.onMouseEnter) props.onMouseEnter(e)
        }
        popover.onmouseleave = (e)=>{if (props.onMouseLeave) props.onMouseLeave(e)};

        this._popoverElement = popover;

        // @ts-ignore
        document.querySelector(this.props.rootSelector).appendChild( this._popoverElement );

        gd.layout.popovers.register(this)
    }

    componentDidMount() {
        this._renderPopover();

        gd.bus.subscribe(BUS_KEYBOARD_ESCAPE, this, this.onKeyboardEscape)
    }

    componentDidUpdate() {
        this._renderPopover();
    }

    componentWillUnmount() {
        gd.layout.popovers.unregister(this)

        gd.bus.unsubscribe(this)

        if (this._tether) this._tether.destroy();
        ReactDOM.unmountComponentAtNode( this._popoverElement );
        if ( this._popoverElement.parentNode ) {
            this._popoverElement.parentNode.removeChild( this._popoverElement );
        }

        this.props.onPopoverHide?.()
    }

    // Tether only follows scroll and resize on its own - anything else that moves the target has to
    // ask for this (see Popovers.repositionAll)
    reposition = () => {
        if (this._tether) this._tether.position()
    }

    // Experimental
    onKeyboardEscape = () => {
        const evt = {}
        this.props.onClickOutside && this.props.onClickOutside(evt)
    }

    onClickOutside = (evt:React.SyntheticEvent) => {
        if (this.props.onClickOutside) this.props.onClickOutside(evt);
    };

    onMouseEnter = (e:React.SyntheticEvent)=>{
        if (this.props.onMouseEnter) this.props.onMouseEnter(e)
    }

    _popoverComponent = (tetherOptions:any) => {
        let res;
        switch (this.props.heightRestriction) {
            case PopoverHeightRestriction.WINDOW_HEIGHT:
                res = <VScroll containerStyle={{maxHeight:window.innerHeight - 20}}>{this.props.children}</VScroll>
                break

            case PopoverHeightRestriction.HALF_WINDOW_HEIGHT:
                res = <VScroll containerStyle={{maxHeight:(window.innerHeight / 2) - 20}}>{this.props.children}</VScroll>
                break

            default:
                res = this.props.children;
                break
        }

        const showPopoverArrow = this.props.enableArrows && this.props.arrowsVersion === 2

        return (
            <PopoverContainer ref={this.props.onContainerReference}
                              eventTypes={this.props.clickOutsideEventTypes}
                              onClickOutside={this.onClickOutside}
                              outsideClickIgnoreClass={this.props.outsideClickIgnoreClass}
                              onKeyDown={this.props.onKeyDown}
                              tetherOptions={tetherOptions}
                              popoverProps={this.props}>
              <div className={Classes("popover-content", this.props.className)} style={this.props.popoverContentStyle}>
                  {showPopoverArrow && <div ref={this.popoverArrowRef} className="popover-arrow"/>}
                  {res}
              </div>
            </PopoverContainer>
        );
    };

    _updatePopoverArrow = (...args:any) => {
        debug && console.log(debug, '_updatePopoverArrow', args)

        if (!this.popoverArrowRef.current || !this._tether) return

        const arrowEl = this.popoverArrowRef.current
        const classes  = this._tether.element.className

        debug && console.log(debug, '_updatePopoverArrow classes', classes)

        // Align the arrow tip with the anchor after any repositioning or pinning.
        // CSS uses fixed offsets which break when Tether shifts the popover away from its
        // nominal attachment point (e.g. pinned to window edge).
        const container = arrowEl.closest('.popover-container')
        if (!container || !this._tether.target) return

        const targetRect    = (this._tether.target as HTMLElement).getBoundingClientRect()
        const containerRect = (container as HTMLElement).getBoundingClientRect()

        arrowEl.style.display = popoverArrow_overlapsTarget(targetRect, containerRect) ? 'none' : ''

        const aligned = popoverArrow_align({
            targetRect      : targetRect,
            containerRect   : containerRect,
            arrowSize       : arrowEl.offsetWidth,
            classes         : classes,
            arrowTarget     : this.props.arrowTarget,
            arrowOffset     : this.props.arrowPointOffset,
            edgeInset       : _.isNil(this.props.arrowEdgeInset) ? undefined : {
                inset           : this.props.arrowEdgeInset,
                targetOffset    : this.props.position.targetOffset,
                offset          : this.props.position.offset,
            },
        })

        if (aligned.axis === 'x') {
            arrowEl.style.left   = aligned.value + 'px'
            arrowEl.style.right  = 'auto'
            arrowEl.style.top    = ''
            arrowEl.style.bottom = ''
        } else {
            arrowEl.style.top    = aligned.value + 'px'
            arrowEl.style.bottom = 'auto'
            arrowEl.style.left   = ''
            arrowEl.style.right  = ''
        }
    }

    _tetherOptions = () => {
        let targetDOMNode;



        if (this.props.getTargetFunc) {
            targetDOMNode = this.props.getTargetFunc();
        } else if (this.props.targetClass) {
            // @ts-ignore
            targetDOMNode = ReactDOM.findDOMNode(this).parentElement.querySelector("." + this.props.targetClass);
        } else {
            // just parent...
            // @ts-ignore
            targetDOMNode = ReactDOM.findDOMNode(this).parentElement
        }


        return {
            element         : this._popoverElement,
            // @ts-ignore
            bodyElement     : document.querySelector(this.props.rootSelector),
            target          : targetDOMNode,
            attachment      : this.props.position.attachment,
            targetAttachment: this.props.position.targetAttachment,
            targetOffset    : this.props.position.targetOffset,
            offset          : this.props.position.offset,
            optimizations: {
                moveElement: false
            },
            constraints: this.props.constraints
        };
    };

    _renderPopover = () => {
        const tetherOptions = this._tetherOptions()
        if (!tetherOptions.target) return

        ReactDOM.render(
            this._popoverComponent(tetherOptions),
            this._popoverElement,
            ()=>{
                if ( this._tether != null ) {
                    this._tether.setOptions(tetherOptions);
                } else if ( window && document ) {
                    const Tether = require( "tether" );
                    this._tether = popoverArrow_keepAttachClassesWhenPinned(new Tether(tetherOptions))
                    this._tether.on('repositioned', this._updatePopoverArrow)
                    this._tether.position() // re-run so the pinned-class fix applies to the constructor's first positioning
                }
                this._updatePopoverArrow()
            }
        )
    }

    render() {
        return <span/>;
    }
}
